import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Box,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';

const Navbar = ({
  hideLoginButton = false,
  enableScrollEffect = true,
  customStyles = {},
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    if (!enableScrollEffect) return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enableScrollEffect]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const navItems = ['Products', 'Features', 'Use Cases', 'Security', 'Pricing'];

  const handleNavClick = (item) => {
    // Handle navigation for new menu items
    if (item === 'Products') {
      // Scroll to products section or navigate
      const productsSection = document.getElementById('products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (item === 'Features') {
      const featuresSection = document.getElementById('features');
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (item === 'Use Cases') {
      const useCasesSection = document.getElementById('use-cases');
      if (useCasesSection) {
        useCasesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (item === 'Security') {
      const securitySection = document.getElementById('security');
      if (securitySection) {
        securitySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (item === 'Pricing') {
      navigate('/pricing');
    }
  };

  const getAppBarStyles = () => ({
    backgroundColor: 'transparent',
    background: enableScrollEffect && scrolled
      ? 'linear-gradient(180deg, rgba(15, 15, 25, 0.95) 0%, rgba(10, 10, 20, 0.9) 100%)'
      : 'transparent',
    backdropFilter: enableScrollEffect && scrolled ? 'blur(20px)' : 'none',
    transition: 'all 0.3s ease',
    boxShadow: 'none',
    border: 'none',
    ...customStyles.appBar,
  });

  const getToolbarStyles = () => ({
    justifyContent: 'space-between',
    background: 'transparent',
    px: { xs: 2, sm: 3, md: 4, lg: 6 },
    py: { xs: 1.5, sm: 2 },
    ...customStyles.toolbar,
  });

  return (
    <>
      <AppBar position="fixed" sx={getAppBarStyles()}>
        <Toolbar sx={getToolbarStyles()}>
          {/* Logo Section - Left */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {/* Logo Icon - Placeholder for HYPERMINDS icon */}
            <Box
              sx={{
                width: { xs: 32, md: 40 },
                height: { xs: 32, md: 40 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #9333EA 0%, #EC4899 100%)',
                borderRadius: '8px',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.8) 0%, rgba(236, 72, 153, 0.8) 100%)',
                  borderRadius: '8px',
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '18px', md: '24px' },
                  fontWeight: 700,
                  color: '#ffffff',
                  zIndex: 1,
                  position: 'relative',
                }}
              >
                H
              </Typography>
            </Box>
            
            {/* Logo Text */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#ffffff',
                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                  lineHeight: 1,
                  letterSpacing: '0.02em',
                }}
              >
                HYPERMINDS
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                  fontWeight: 500,
                  letterSpacing: '0.05em',
                  lineHeight: 1.2,
                  mt: 0.25,
                }}
              >
                OUT THINK. OUT DO. OUT DELIVER.
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation - Centered */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                gap: { md: 3, lg: 4 },
                alignItems: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                ...customStyles.navigationBox,
              }}
            >
              {navItems.map((item) => (
                <Link
                  key={item}
                  variant="body1"
                  onClick={() => handleNavClick(item)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    textTransform: 'none',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: { md: '0.9rem', lg: '1rem' },
                    letterSpacing: '0.01em',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#ffffff',
                    },
                    ...customStyles.navLink,
                  }}
                >
                  {item}
                </Link>
              ))}
            </Box>
          )}

          {/* Desktop CTA Buttons - Right */}
          {!isMobile && !hideLoginButton && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
              <Link
                onClick={() => navigate('/login')}
                sx={{
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: { md: '0.9rem', lg: '1rem' },
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                }}
              >
                Sign In
              </Link>
              <Button
                variant="contained"
                onClick={() => navigate('/contact')}
                sx={{
                  background: 'linear-gradient(90deg, #9333EA 0%, #4F46E5 100%)',
                  color: '#ffffff',
                  borderRadius: '40px',
                  padding: { md: '0.5rem 1.25rem', lg: '0.625rem 1.5rem' },
                  fontWeight: 600,
                  fontSize: { md: '0.875rem', lg: '0.9375rem' },
                  textTransform: 'none',
                  letterSpacing: '0.01em',
                  boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #7E22CE 0%, #4338CA 100%)',
                    boxShadow: '0 6px 20px rgba(147, 51, 234, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  ...customStyles.ctaButton,
                }}
              >
                Get In Touch
              </Button>
            </Box>
          )}

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              sx={{
                color: '#ffffff',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.2)',
                },
              }}
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer 
        anchor="right" 
        open={mobileOpen} 
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: { xs: '70%', sm: '60%' },
            maxWidth: '320px',
            background: 'linear-gradient(180deg, rgba(15, 15, 25, 0.98) 0%, rgba(10, 10, 20, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            py: { xs: 4, sm: 5 },
            px: { xs: 2, sm: 3 },
            color: '#ffffff',
          }}
        >
          <List sx={{ flex: 1, pt: 2 }}>
            {navItems.map((item) => (
              <ListItem
                key={item}
                onClick={() => {
                  handleNavClick(item);
                  handleDrawerToggle();
                }}
                sx={{
                  py: { xs: 2, sm: 2.5 },
                  px: { xs: 2, sm: 2.5 },
                  mb: 0.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                <ListItemText
                  primary={item}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      letterSpacing: '0.01em',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          {!hideLoginButton && (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate('/login');
                  handleDrawerToggle();
                }}
                sx={{
                  width: '100%',
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: '8px',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  textTransform: 'none',
                  letterSpacing: '0.01em',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  navigate('/contact');
                  handleDrawerToggle();
                }}
                sx={{
                  width: '100%',
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: '8px',
                  background: 'linear-gradient(90deg, #9333EA 0%, #4F46E5 100%)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  textTransform: 'none',
                  letterSpacing: '0.01em',
                  boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #7E22CE 0%, #4338CA 100%)',
                    boxShadow: '0 6px 20px rgba(147, 51, 234, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Get In Touch
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

Navbar.propTypes = {
  hideLoginButton: PropTypes.bool,
  enableScrollEffect: PropTypes.bool,
  customStyles: PropTypes.object,
};

export default Navbar;
